import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CasRHFPercentField } from 'src/compass/shared/components';
import { CasRHFRiskWithSalesManagerSelect } from 'src/compass/application/general/components/CasRiskWithSalesManagerSelect';
import { CasRHFSourceSelect } from 'src/compass/application/general/components/CasSourceSelect';
import CasAsOfMultiRowTable from '../CasAsOf/CasAsOfMultiRowTable';

type FieldProps = {
  asofReadonly: boolean;
};

/*
  An as-of can carry any number of Account Sources, splitting the Source Type's rate between
  them. Same multi-row primitive as Additional Compensation just below it on both forms.
*/

/*
  An even split that still totals exactly 100 at the three decimals SplitPercent stores.
  100/3 is not representable, so the rounding remainder goes to the first row:
  3 sources -> 33.334 / 33.333 / 33.333. Without this the server's "must total 100" rule
  would reject its own default.
*/
const evenSplit = (count: number): number[] => {
  if (count <= 0) return [];
  const each = Math.floor((100 / count) * 1000) / 1000;
  const rows = Array<number>(count).fill(each);
  rows[0] = Math.round((100 - each * (count - 1)) * 1000) / 1000;
  return rows;
};

export default function SourceEmployeeAndTypeFields({ asofReadonly }: FieldProps) {
  const { getValues, setValue } = useFormContext();

  const asOfSources = useWatch({ name: 'asOf.asOfSources' });
  const rowCount: number = Array.isArray(asOfSources) ? asOfSources.length : 0;
  const previousRowCount = useRef(rowCount);

  useEffect(() => {
    const previous = previousRowCount.current;
    previousRowCount.current = rowCount;
    if (previous === rowCount || asofReadonly || rowCount === 0) return;

    const percents: number[] = (getValues('asOf.asOfSources') ?? []).map((row: any) =>
      Number(row?.splitPercent)
    );

    // Adding a row leaves it blank; removing one leaves the rest short of 100. Either way the
    // split is no longer valid, so re-spread it evenly. A split the user has already balanced
    // to 100 across every row is left alone.
    const total = percents.reduce((sum, p) => sum + (Number.isFinite(p) ? p : 0), 0);
    const needsReset = percents.some((p) => !p) || Math.abs(total - 100) >= 0.0005;
    if (!needsReset) return;

    evenSplit(rowCount).forEach((percent, index) =>
      setValue(`asOf.asOfSources.${index}.splitPercent`, percent, { shouldDirty: true })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowCount, asofReadonly]);

  return (
    <CasAsOfMultiRowTable
      readOnly={asofReadonly}
      name="asOf.asOfSources"
      rowCountMode={0}
      columns={[
        {
          type: 'component',
          label: 'Source Employee',
          name: 'sourceEmployeeId',
          fullWidth: true,
          component: CasRHFRiskWithSalesManagerSelect,
        },
        {
          type: 'component',
          label: 'Type',
          name: 'sourceId',
          fullWidth: true,
          component: CasRHFSourceSelect,
        },
        {
          type: 'component',
          label: 'Split %',
          name: 'splitPercent',
          component: CasRHFPercentField,
        },
      ]}
    />
  );
}
